import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, Tag, MessageCircle, Share2, Loader2 } from "lucide-react";
import { Logo } from "@/components/landing/shared";
import { api, WHATSAPP_URL } from "@/lib/api";
import { fullUrl } from "@/context/SiteConfigContext";

export default function ArticlePage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} | Manutentudo Gás`;
      })
      .catch(() => setError(true));
    api
      .get("/blog")
      .then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3)))
      .catch(() => {});
    return () => {
      document.title = "Manutentudo Serviços — Gás Encanado SP";
    };
  }, [slug]);

  const shareUrl = `${window.location.origin}/dicas/${slug}`;
  const shareLink = `https://wa.me/?text=${encodeURIComponent(post ? `${post.title} — ${shareUrl}` : shareUrl)}`;

  return (
    <div className="min-h-screen bg-[var(--dark-bg)]" data-testid="article-page">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0C0E12]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-4xl items-center justify-between px-5">
          <Logo />
          <Link
            to="/"
            data-testid="article-back-link"
            className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-display text-xs font-bold uppercase tracking-wider text-zinc-300 transition-colors hover:border-[var(--primary)] hover:text-[var(--primary)]"
          >
            <ArrowLeft size={14} /> Voltar ao site
          </Link>
        </div>
      </header>

      {!post && !error && (
        <div className="flex h-96 items-center justify-center">
          <Loader2 size={30} className="animate-spin text-[var(--primary)]" />
        </div>
      )}

      {error && (
        <div className="mx-auto max-w-4xl px-5 py-24 text-center">
          <p className="font-display text-2xl font-bold uppercase text-white">Artigo não encontrado</p>
          <Link to="/" className="mt-4 inline-block text-sm text-[var(--primary)]">
            Voltar para o site
          </Link>
        </div>
      )}

      {post && (
        <motion.main
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-4xl px-5 pb-28 pt-10"
        >
          <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-500">
            <span className="flex items-center gap-1.5 rounded-full bg-[var(--primary)]/10 px-3 py-1 font-semibold uppercase tracking-wide text-[var(--primary)]">
              <Tag size={12} /> {post.category}
            </span>
            <span className="flex items-center gap-1.5">
              <CalendarDays size={13} /> {post.date}
            </span>
          </div>
          <h1 className="mt-5 font-display text-4xl font-extrabold uppercase leading-[0.95] text-white sm:text-5xl" data-testid="article-title">
            {post.title}
          </h1>
          {post.img && (
            <img
              src={fullUrl(post.img)}
              alt={post.title}
              data-testid="article-image"
              className="mt-8 w-full rounded-2xl border border-white/10 object-cover shadow-2xl"
            />
          )}
          <div className="mt-8 space-y-5 text-base leading-relaxed text-zinc-300 sm:text-lg" data-testid="article-content">
            {post.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="article-cta"
            className="mt-10 flex items-center justify-center gap-2.5 rounded-full bg-[var(--whatsapp)] px-8 py-4 font-display text-base font-bold uppercase tracking-wider text-black shadow-[0_8px_40px_rgba(37,211,102,0.35)] transition-transform duration-300 hover:scale-[1.02]"
          >
            <MessageCircle size={19} strokeWidth={2.5} />
            {post.cta}
          </a>

          {related.length > 0 && (
            <div className="mt-16 border-t border-white/10 pt-10" data-testid="related-articles">
              <h2 className="font-display text-2xl font-bold uppercase text-white">
                Continue <span className="italic text-[var(--primary)]">lendo</span>
              </h2>
              <div className="mt-6 grid gap-5 sm:grid-cols-3">
                {related.map((r, i) => (
                  <Link
                    key={r.slug}
                    to={`/dicas/${r.slug}`}
                    data-testid={`related-card-${i}`}
                    className="group overflow-hidden rounded-2xl border border-white/10 bg-[#11141A] transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-[var(--primary)]/50"
                  >
                    {r.img && (
                      <img src={fullUrl(r.img)} alt={r.title} loading="lazy" className="aspect-[1000/650] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    )}
                    <div className="p-4">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--primary)]">{r.category}</span>
                      <p className="mt-1.5 font-display text-sm font-bold uppercase leading-tight text-white">{r.title}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </motion.main>
      )}

      {post && (
        <motion.a
          href={shareLink}
          target="_blank"
          rel="noreferrer"
          data-testid="article-share-button"
          aria-label="Compartilhar no WhatsApp"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.8, type: "spring", stiffness: 200 }}
          className="pulse-ring fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--whatsapp)] text-black shadow-[0_10px_35px_rgba(37,211,102,0.45)] transition-transform duration-300 hover:scale-110"
        >
          <Share2 size={24} />
        </motion.a>
      )}
    </div>
  );
}
